import Point from '@mapbox/point-geometry';
import {VectorTileFeature} from '@mapbox/vector-tile';
const toGeoJSON = VectorTileFeature.prototype.toGeoJSON;
import EXTENT from '../style-spec/data/extent';

import type {VectorTile, VectorTileLayer} from '@mapbox/vector-tile';

type VectorTileFeatureLike = Pick<VectorTileFeature, 'properties' | 'extent' | 'type' | 'id' | 'loadGeometry' | 'toGeoJSON'>;
type VectorTileLayerLike = Pick<VectorTileLayer, 'name' | 'extent' | 'length' | 'feature'>;

// The feature type used by geojson-vt and supercluster. Should be extracted to
// global type and used in module definitions for those two modules.
export type Feature = {
    type: 1;
    id: unknown;
    tags: {
        [_: string]: string | number | boolean;
    };
    geometry: Array<[number, number]>;
} | {
    type: 2 | 3;
    id: unknown;
    tags: {
        [_: string]: string | number | boolean;
    };
    geometry: Array<Array<[number, number]>>;
};

class FeatureWrapper implements VectorTileFeatureLike {
    _feature: Feature;

    extent: number;
    type: 1 | 2 | 3;
    id: number;
    properties: {
        [_: string]: string | number | boolean;
    };

    constructor(feature: Feature) {
        this._feature = feature;

        this.extent = EXTENT;
        this.type = feature.type;
        this.properties = feature.tags;

        // If the feature has a top-level `id` property, copy it over, but only
        // if it can be coerced to an integer, because this wrapper is used for
        // serializing geojson feature data into vector tile PBF data, and the
        // vector tile spec only supports integer values for feature ids --
        // allowing non-integer values here results in a non-compliant PBF
        // that causes an exception when it is parsed with vector-tile-js
        if ('id' in feature && !isNaN(feature.id as number)) {
            this.id = parseInt(feature.id as string, 10);
        }
    }

    loadGeometry(): Array<Array<Point>> {
        if (this._feature.type === 1) {
            const geometry: Array<Array<Point>> = [];
            for (const point of this._feature.geometry) {
                geometry.push([new Point(point[0], point[1])]);
            }
            return geometry;
        } else {
            const geometry: Array<Array<Point>> = [];
            for (const ring of this._feature.geometry) {
                const newRing: Array<Point> = [];
                for (const point of ring) {
                    newRing.push(new Point(point[0], point[1]));
                }
                geometry.push(newRing);
            }
            return geometry;
        }
    }

    toGeoJSON(x: number, y: number, z: number): GeoJSON.Feature {
        return toGeoJSON.call(this, x, y, z);
    }
}

class LayerWrapper implements VectorTileLayerLike {
    name: string;
    extent: number;
    length: number;
    _jsonFeatures: Array<Feature>;

    constructor(name: string, features: Array<Feature>) {
        this.name = name;
        this.extent = EXTENT;
        this.length = features.length;
        this._jsonFeatures = features;
    }

    feature(i: number): VectorTileFeature {
        return new FeatureWrapper(this._jsonFeatures[i] as Feature) as unknown as VectorTileFeature;
    }
}

class GeoJSONWrapper implements VectorTile {
    layers: Record<string, VectorTileLayer>;
    extent: number;

    constructor(layers: {[_: string]: Array<Feature>}) {
        this.layers = {};
        this.extent = EXTENT;

        for (const name of Object.keys(layers)) {
            this.layers[name] = new LayerWrapper(name, layers[name] as Array<Feature>) as unknown as VectorTileLayer;
        }
    }
}

export default GeoJSONWrapper;
